import { useRef, type ReactNode } from "react";
import { BotonLink, Button, Logo2M } from "@/components";
import type { PresentacionProyecto } from "./newbery3563";

/**
 * La presentación comercial de Proyecto ("Bienvenidos a Newbery 3563", Mati, 2026-11-03): portada, relato del
 * edificio y del barrio con los renders de la carpeta comercial, las plantas, y un cierre que invita a sumarse.
 * Sólo presentación: el estado (planta elegida, compartir) vive en useProyectoTab.
 */

/** En el celular el bloque va de borde a borde (el main tiene px-4); en la compu, tarjeta redondeada. */
const A_SANGRE = "-mx-4 md:mx-0 md:rounded-tarjeta";

function Antetitulo({ children, claro = false }: { children: ReactNode; claro?: boolean }) {
  return <p className={`text-[0.6875rem] font-medium uppercase tracking-[0.28em] ${claro ? "text-crema/70" : "text-texto-suave"}`}>{children}</p>;
}

function Titular({ antes, destacado, claro = false, id }: { antes: string; destacado: string; claro?: boolean; id?: string }) {
  return (
    <h2 id={id} className="text-[clamp(2.25rem,5.5vw,3.75rem)] font-light leading-[1.02] tracking-[-0.03em]">
      {antes} <span className={`block ${claro ? "text-marca-clara" : "text-marca"}`}>{destacado}</span>
    </h2>
  );
}

/** El encabezado de "Elegí tu unidad", con el mismo estilo que el resto de la presentación. */
export function EncabezadoUnidades() {
  return (
    <div className="flex flex-col gap-4 border-t border-borde pt-10">
      <Antetitulo>Disponibilidad</Antetitulo>
      <Titular antes="Elegí" destacado="tu unidad" />
      <p className="max-w-xl text-texto-suave">
        Tocá un piso o una unidad para ver sus imágenes, su superficie y hacia dónde mira. Lo vendido y reservado se ve en el momento.
      </p>
    </div>
  );
}

const CLASE_BOTON_MARCA = "rounded-full bg-marca px-6 text-white hover:bg-marca-hover";
const CLASE_BOTON_CONTORNO = "rounded-full border-current bg-transparent px-6 text-current hover:bg-white/10";

function IconoColectivo() {
  return (
    <svg aria-hidden viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="12" height="12" rx="2" />
      <path d="M4 10h12M7 15v2m6-2v2M7 12.5h.01m6 0h.01" />
    </svg>
  );
}

export function IconoDescarga() {
  return (
    <svg aria-hidden viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3v10m0 0-4-4m4 4 4-4M4 16h12" />
    </svg>
  );
}

// ------------------------------------------------------------------ portada

export interface PortadaProps {
  p: PresentacionProyecto;
  /** "4 de 12 departamentos disponibles", o null mientras cargan las unidades. */
  disponibles: { cantidad: number; detalle: string } | null;
}

export function PortadaProyecto({ p, disponibles }: PortadaProps) {
  const { portada } = p;
  return (
    <section
      aria-labelledby="titulo-proyecto"
      className={`${A_SANGRE} -mt-6 grid overflow-hidden bg-crema text-carbon md:mt-0 md:min-h-[38rem] md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]`}
    >
      {/* El render entero, de la terraza a la planta baja (Mati, 2026-11-03: abajo se cortaba). La caja tiene su
          proporción: si el texto la estira en la compu, se recortan los costados y nunca arriba ni abajo. */}
      <div className="relative aspect-[1300/1554] md:order-2">
        <img
          src={portada.imagen}
          alt="Render del frente de Newbery 3563 al atardecer, con sus balcones curvos y la planta baja"
          className="absolute inset-0 size-full object-cover object-[38%_100%]"
          fetchPriority="high"
        />
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-crema to-transparent md:hidden" />
      </div>
      <div className="relative z-[1] flex flex-col gap-8 px-6 pb-10 pt-4 motion-safe:animate-aparecer md:justify-between md:p-12 lg:p-16">
        <Antetitulo>{portada.zona}</Antetitulo>
        <div>
          <p className="mb-2 text-subtitulo font-light text-carbon/70">Bienvenidos a</p>
          <h1 id="titulo-proyecto" className="text-[clamp(3.5rem,10vw,7rem)] font-light leading-[0.92] tracking-[-0.045em]">
            {portada.nombre} <span className="block text-marca">{portada.numero}</span>
          </h1>
          <span aria-hidden className="my-7 block h-px w-16 bg-marca" />
          <p className="max-w-xs text-[1.125rem] leading-relaxed text-carbon/85">{portada.bajada}</p>
        </div>
        <div className="flex flex-col gap-5">
          {disponibles && (
            <p className="inline-flex w-fit items-center gap-2.5 rounded-full border border-carbon/15 bg-white/60 px-4 py-1.5 text-chico backdrop-blur">
              <span aria-hidden className="relative flex size-2">
                <span className="absolute inline-flex size-full rounded-full bg-exito opacity-60 motion-safe:animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-exito" />
              </span>
              <span>
                <strong className="font-semibold tabular-nums">{disponibles.cantidad}</strong> {disponibles.detalle}
              </span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <BotonLink href="#unidades" variante="primario" className={CLASE_BOTON_MARCA}>
              Elegí tu unidad
            </BotonLink>
            {/* En el celular baja la versión vertical; en la compu, la apaisada. */}
            {[
              { c: p.carpetaMovil, vista: "md:hidden" },
              { c: p.carpeta, vista: "hidden md:inline-flex" },
            ].map(({ c, vista }) => (
              <BotonLink
                key={c.archivo}
                href={c.url}
                download={c.archivo}
                className={`gap-2 rounded-full border-carbon/30 bg-transparent px-5 text-carbon hover:bg-carbon/5 ${vista}`}
              >
                <IconoDescarga />
                Carpeta comercial
              </BotonLink>
            ))}
          </div>
          <p className="text-chico text-carbon/60">
            {portada.creditos.map((c, i) => (
              <span key={c.rol}>
                {i > 0 && " · "}
                {c.rol} <span className="text-carbon">{c.quien}</span>
              </span>
            ))}
          </p>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ el relato: edificio, espacios, barrio y plantas

export interface RelatoProps {
  p: PresentacionProyecto;
  planta: string;
  onElegirPlanta: (clave: string) => void;
  /** La imagen de "Por dentro" que se ve (índice en `p.espacios`). */
  espacio: number;
  onElegirEspacio: (i: number) => void;
}

export function RelatoProyecto({ p, planta, onElegirPlanta, espacio, onElegirEspacio }: RelatoProps) {
  const tira = useRef<HTMLUListElement>(null);
  const actual = p.espacios[espacio] ?? p.espacios[0]!;
  const irA = (i: number) => {
    const el = tira.current;
    onElegirEspacio(i);
    el?.scrollTo?.({ left: i * el.clientWidth, behavior: "smooth" });
  };
  // Deslizando con el dedo: la que quedó a la vista pasa a ser la actual (para el texto, la cuenta y las miniaturas).
  const alDeslizar = () => {
    const el = tira.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== espacio) onElegirEspacio(i);
  };
  const elegida = p.plantas.find((x) => x.clave === planta) ?? p.plantas[0]!;
  const { ubicacion, mesa, barrio, edificio } = p;

  return (
    <>
      {/* Vivir entre curvas y luz */}
      <figure className={`${A_SANGRE} relative overflow-hidden`}>
        <img
          src={p.frase.imagen}
          alt="Balcones curvos de hormigón con barandas de hierro, vistos desde abajo contra el cielo"
          loading="lazy"
          className="aspect-[4/5] w-full object-cover object-[30%_50%] sm:aspect-[16/9] md:aspect-[21/9]"
        />
        <figcaption className="absolute bottom-5 left-5 rounded-tarjeta bg-crema/95 px-5 py-3 text-[clamp(1.25rem,3vw,1.75rem)] font-light tracking-tight text-carbon shadow-flotante md:bottom-8 md:left-8">
          {p.frase.antes}
          <span className="text-marca">{p.frase.destacado}</span>
          {p.frase.despues}
        </figcaption>
      </figure>

      {/* 01 — El edificio */}
      <section aria-labelledby="titulo-edificio" className="grid gap-8 py-6 md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] md:items-center md:gap-12 md:py-10">
        <div className="flex flex-col gap-6">
          <Antetitulo>01 — El edificio</Antetitulo>
          <Titular id="titulo-edificio" antes={edificio.titulo} destacado={edificio.destacado} />
          <p className="max-w-xl text-[1.0625rem] leading-relaxed text-texto/85">{edificio.texto}</p>
          <dl className="grid w-fit grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-chico text-texto-suave">
            {p.portada.creditos.map((c) => (
              <div key={c.rol} className="contents">
                <dt>{c.rol}</dt>
                <dd className="text-texto">{c.quien}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="overflow-hidden rounded-tarjeta bg-carbon">
          <img src={edificio.boceto} alt="" loading="lazy" className="mx-auto aspect-[4/5] w-full object-cover object-left-top opacity-90" />
        </div>
      </section>

      {/* En números */}
      <section aria-labelledby="titulo-numeros" className={`${A_SANGRE} bg-marca px-6 py-10 text-white md:px-12 md:py-14`}>
        <h2 id="titulo-numeros" className="mb-8">
          <Antetitulo claro>01 — En números</Antetitulo>
        </h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-3 md:gap-x-10">
          {p.numeros.map((n) => (
            <div key={n.texto} className="flex flex-col-reverse gap-1 border-t border-white/40 pt-4">
              <dt className="text-chico text-white/85">{n.texto}</dt>
              <dd className="text-[clamp(2.5rem,7vw,4.5rem)] font-extralight leading-none tracking-[-0.04em] tabular-nums">{n.cifra}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Por dentro: la galería grande, de a una imagen (Mati, 2026-11-03: en la tira chica se pasaban por alto) */}
      <section aria-labelledby="titulo-espacios" className="flex flex-col gap-6 py-6 md:py-10">
        <div className="flex flex-col gap-4">
          <Antetitulo>Por dentro</Antetitulo>
          <Titular id="titulo-espacios" antes="Espacios para" destacado="vivir afuera y adentro" />
        </div>
        <div role="region" aria-roledescription="galería" aria-label="Renders de los espacios" className="flex flex-col gap-4">
          <div className="relative -mx-4 md:mx-0">
            <ul ref={tira} onScroll={alDeslizar} className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] md:rounded-tarjeta">
              {p.espacios.map((e, i) => (
                <li key={e.titulo} aria-label={`${i + 1} de ${p.espacios.length}: ${e.titulo}`} className="w-full shrink-0 snap-center">
                  <img src={e.src} alt={e.titulo} loading={i === 0 ? "eager" : "lazy"} className="aspect-[4/3] w-full object-cover sm:aspect-[16/9]" />
                </li>
              ))}
            </ul>
            <Button
              variante="secundario"
              aria-label="Imagen anterior"
              disabled={espacio === 0}
              onClick={() => irA(espacio - 1)}
              className="absolute left-3 top-1/2 hidden size-12 -translate-y-1/2 rounded-full border-0 bg-crema/90 p-0 text-[1.5rem] text-carbon shadow-flotante hover:bg-crema disabled:opacity-0 sm:inline-flex"
            >
              ‹
            </Button>
            <Button
              variante="secundario"
              aria-label="Imagen siguiente"
              disabled={espacio === p.espacios.length - 1}
              onClick={() => irA(espacio + 1)}
              className="absolute right-3 top-1/2 hidden size-12 -translate-y-1/2 rounded-full border-0 bg-crema/90 p-0 text-[1.5rem] text-carbon shadow-flotante hover:bg-crema disabled:opacity-0 sm:inline-flex"
            >
              ›
            </Button>
          </div>
          <div className="flex items-end justify-between gap-4">
            <p aria-live="polite" className="flex flex-col gap-1">
              <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-marca">{actual.donde}</span>
              <span className="text-[clamp(1.25rem,3vw,1.75rem)] font-light leading-tight tracking-tight">{actual.titulo}</span>
            </p>
            <span className="shrink-0 text-chico tabular-nums text-texto-suave">
              {espacio + 1} / {p.espacios.length}
            </span>
          </div>
          <div role="group" aria-label="Elegir imagen" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:px-0">
            {p.espacios.map((e, i) => (
              <button
                key={e.titulo}
                type="button"
                aria-label={`Ver ${e.titulo}`}
                aria-current={i === espacio}
                onClick={() => irA(i)}
                className={`shrink-0 overflow-hidden rounded-control transition ${i === espacio ? "opacity-100 ring-2 ring-marca ring-offset-2 ring-offset-fondo" : "opacity-55 hover:opacity-90"}`}
              >
                <img src={e.src} alt="" loading="lazy" className="h-14 w-20 object-cover md:h-16 md:w-24" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 02 — Chacarita */}
      <section aria-labelledby="titulo-barrio" className={`${A_SANGRE} bg-carbon px-6 py-12 text-crema md:px-12 md:py-16`}>
        <div className="flex flex-col gap-6">
          <Antetitulo claro>02 — Chacarita</Antetitulo>
          <Titular id="titulo-barrio" antes={barrio.titulo} destacado={barrio.destacado} claro />
        </div>
        <ul className="mt-10 grid gap-8 md:grid-cols-3 md:gap-10">
          {barrio.prensa.map((x) => (
            <li key={x.fuente} className="flex flex-col gap-3 border-t border-marca-clara pt-5">
              <span className="text-[clamp(2.75rem,6vw,4rem)] font-extralight leading-none tracking-[-0.03em] text-marca-clara">{x.cifra}</span>
              <span className="text-cuerpo leading-relaxed text-crema/90">{x.texto}</span>
              <span className="text-[0.6875rem] font-medium uppercase tracking-[0.2em] text-crema/55">{x.fuente}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Ubicación */}
      <section aria-labelledby="titulo-ubicacion" className="grid gap-8 py-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:items-start md:gap-12 md:py-10">
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ubicacion.calle}, ${ubicacion.barrio}, CABA`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block overflow-hidden rounded-tarjeta"
        >
          <img
            src={ubicacion.mapa}
            alt={`Mapa del barrio con ${ubicacion.calle} marcada, el subte B, el tren Urquiza y las plazas cercanas`}
            loading="lazy"
            className="aspect-square w-full object-cover"
          />
          <span className="absolute right-3 top-3 rounded-full bg-carbon/85 px-3 py-1 text-chico text-crema transition-colors group-hover:bg-marca">
            Abrir en Google Maps ↗
          </span>
        </a>
        <div className="flex flex-col gap-6">
          <Antetitulo>02 — Ubicación</Antetitulo>
          <h2 id="titulo-ubicacion" className="text-[clamp(1.75rem,4vw,2.5rem)] font-light leading-tight tracking-tight">
            {ubicacion.calle} <span className="block text-marca">{ubicacion.barrio}</span>
          </h2>
          <BloqueUbicacion titulo="Conectividad">
            <p className="font-medium">{ubicacion.conectividad.texto}</p>
            <ul className="flex flex-col">
              {ubicacion.conectividad.avenidas.map((x) => (
                <li key={x.nombre} className="flex items-baseline justify-between gap-4 border-b border-borde py-2 last:border-b-0">
                  <span>
                    <span className="font-medium">{x.nombre}</span> <span className="text-chico text-texto-suave">· {x.hacia}</span>
                  </span>
                  <span className="shrink-0 text-chico font-medium text-marca">{x.distancia}</span>
                </li>
              ))}
            </ul>
          </BloqueUbicacion>
          <BloqueUbicacion titulo="Transporte">
            <ul className="flex flex-col gap-3">
              {ubicacion.transporte.map((x) => (
                <li key={x.nombre} className="flex items-start gap-3">
                  <span aria-hidden className="flex shrink-0 gap-1">
                    {/* Sin siglas, el colectivo: va el ícono, como en la carpeta. */}
                    {(x.siglas.length ? x.siglas : [null]).map((sigla) => (
                      <span key={sigla ?? "colectivo"} className="grid size-8 place-items-center rounded-full bg-carbon text-chico font-semibold text-crema">
                        {sigla ?? <IconoColectivo />}
                      </span>
                    ))}
                  </span>
                  <span>
                    <span className="block font-medium">{x.nombre}</span>
                    <span className="text-chico text-texto-suave">{x.detalle}</span>
                  </span>
                </li>
              ))}
            </ul>
          </BloqueUbicacion>
          <BloqueUbicacion titulo="Espacios verdes y educación">
            <ul>
              {ubicacion.verdes.map((x) => (
                <li key={x.nombre}>
                  <span className="font-medium">{x.nombre}</span> <span className="text-texto-suave">· {x.detalle}</span>
                </li>
              ))}
            </ul>
            <p>{ubicacion.educacion}</p>
          </BloqueUbicacion>
          <p className="text-chico text-texto-suave">{ubicacion.nota}</p>
        </div>
      </section>

      {/* La mesa, a la vuelta */}
      <section aria-labelledby="titulo-mesa" className="grid gap-8 border-t border-borde py-10 md:grid-cols-2 md:gap-12">
        <div className="flex flex-col gap-6">
          <Antetitulo>02 — A pasos</Antetitulo>
          <Titular id="titulo-mesa" antes={mesa.titulo} destacado={mesa.destacado} />
          <p className="max-w-sm text-texto-suave">{mesa.texto}</p>
        </div>
        <ul className="self-center border-t border-borde">
          {mesa.lugares.map((l) => (
            <li key={l.nombre} className="flex items-baseline justify-between gap-4 border-b border-borde py-3">
              <span>
                <span className="font-medium">{l.nombre}</span> <span className="text-texto-suave">· {l.tipo}</span>
              </span>
              <span className="shrink-0 font-medium tabular-nums text-marca">{l.minutos} min</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 03 — Unidades y planos */}
      <section aria-labelledby="titulo-plantas" className={`${A_SANGRE} flex flex-col gap-6 bg-crema px-4 py-10 text-carbon md:px-12 md:py-14`}>
        <div className="flex flex-col gap-6">
          <Antetitulo>03 — Unidades y planos</Antetitulo>
          <Titular id="titulo-plantas" antes="Cada planta," destacado="a tu medida" />
        </div>
        <div role="group" aria-label="Plantas" className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0">
          {p.plantas.map((x) => (
            <Button
              key={x.clave}
              variante="secundario"
              tamanio="sm"
              aria-pressed={x.clave === elegida.clave}
              onClick={() => onElegirPlanta(x.clave)}
              className={`shrink-0 rounded-full px-4 font-normal ${x.clave === elegida.clave ? "border-marca bg-marca text-white hover:bg-marca" : "border-carbon/20 bg-transparent text-carbon hover:bg-carbon/5"}`}
            >
              {x.etiqueta}
            </Button>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-center">
          <img
            key={elegida.clave}
            src={elegida.src}
            alt={`Planta: ${elegida.titulo}`}
            loading="lazy"
            className="w-full rounded-control motion-safe:animate-aparecer"
          />
          <div className="flex flex-col gap-4">
            <h3 className="text-[1.75rem] font-light tracking-tight">{elegida.titulo}</h3>
            <p className="text-carbon/75">{elegida.bajada}</p>
            {elegida.superficies.length > 0 && (
              <table className="w-full text-left text-chico">
                <caption className="sr-only">Superficies de {elegida.titulo}</caption>
                <thead>
                  <tr className="border-b border-carbon/40 text-[0.625rem] uppercase tracking-[0.18em] text-carbon/60">
                    <th scope="col" className="py-2 font-medium">
                      Unidad
                    </th>
                    <th scope="col" className="py-2 text-right font-medium">
                      Cubierto
                    </th>
                    <th scope="col" className="py-2 text-right font-medium">
                      Descubierto
                    </th>
                    <th scope="col" className="py-2 text-right font-medium">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {elegida.superficies.map((s) => (
                    <tr key={s.unidad} className="border-b border-carbon/15">
                      <th scope="row" className="py-2.5 text-cuerpo font-normal">
                        {s.unidad}
                      </th>
                      <td className="py-2.5 text-right tabular-nums">{s.cubierto}</td>
                      <td className="py-2.5 text-right tabular-nums">{s.descubierto}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-marca">{s.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-chico text-carbon/55">Superficies aproximadas. Las medidas de los planos pueden sufrir modificaciones por razones técnicas.</p>
          </div>
        </div>
      </section>
    </>
  );
}

function BloqueUbicacion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-t border-borde pt-4">
      <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.22em] text-marca">{titulo}</h3>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ cierre

export interface CierreProps {
  p: PresentacionProyecto;
  onCompartir: () => void;
  avisoCompartir: string | null;
}

export function CierreProyecto({ p, onCompartir, avisoCompartir }: CierreProps) {
  return (
    <section aria-labelledby="titulo-cierre" className={`${A_SANGRE} flex flex-col gap-10 bg-marca px-6 py-14 text-white md:px-12 md:py-20`}>
      <div className="flex flex-col gap-5">
        <h2 id="titulo-cierre" className="text-[clamp(3rem,9vw,6rem)] font-extralight leading-[0.95] tracking-[-0.045em]">
          {p.cierre.titulo} <span className="block">{p.cierre.destacado}</span>
        </h2>
        <p className="max-w-md text-[1.125rem] font-light leading-relaxed text-white/90">
          Sé parte de {p.portada.nombre} {p.portada.numero}. Elegí tu unidad, llevate la carpeta comercial y compartila con quien quieras sumar.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <BotonLink href="#unidades" className="rounded-full border-white bg-white px-6 text-marca hover:bg-crema">
          Elegí tu unidad
        </BotonLink>
        <BotonLink href={p.carpeta.url} download={p.carpeta.archivo} className={`gap-2 ${CLASE_BOTON_CONTORNO}`}>
          <IconoDescarga />
          Descargar carpeta comercial ({p.carpeta.peso})
        </BotonLink>
        <BotonLink href={p.carpetaMovil.url} download={p.carpetaMovil.archivo} className={`gap-2 ${CLASE_BOTON_CONTORNO}`}>
          <IconoDescarga />
          Carpeta para celular ({p.carpetaMovil.peso})
        </BotonLink>
        <Button variante="secundario" className={CLASE_BOTON_CONTORNO} onClick={onCompartir}>
          Compartir
        </Button>
      </div>
      {avisoCompartir && (
        <p role="status" className="text-chico text-white/90">
          {avisoCompartir}
        </p>
      )}
      <div className="flex flex-col gap-2 border-t border-white/30 pt-6 text-chico text-white/75">
        <Logo2M className="mb-2 w-24 text-white" />
        <p>
          {p.portada.creditos.map((c, i) => (
            <span key={c.rol}>
              {i > 0 && " · "}
              {c.rol} <span className="text-white">{c.quien}</span>
            </span>
          ))}
        </p>
        <p>{p.legal}</p>
      </div>
    </section>
  );
}
